"use client"

import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Paper,
} from "@mui/material"
import SaveIcon from "@mui/icons-material/Save"
import CloseIcon from "@mui/icons-material/Close"
import { useEffect, useState, useMemo, useCallback } from "react"
import MUIDataTable from "mui-datatables"
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined"

const initialState = {
  tipo: "",
  numero: "",
}

const formatPhoneNumber = (phone) => {
  const parts = []
  if (phone.tipo) parts.push(phone.tipo)
  if (phone.numero) parts.push(phone.numero)
  return parts.join(": ")
}

const TelefonosDialog = ({ onSave, onClose, itemCrud, itemsCrud, actionCrud, setMessage, setShowMessage }) => {
  const [itemCrudLocal, setItemCrudLocal] = useState(initialState)
  const [telefonos, setTelefonos] = useState([])
  const [selectedRow, setSelectedRow] = useState(null)

  useEffect(() => {
    if (actionCrud === "create") {
      setItemCrudLocal(initialState)
    } else {
      setItemCrudLocal(itemCrud)
    }

    const _itemCrud = itemsCrud
    if (_itemCrud.length > 0) 
      setTelefonos(_itemCrud)
    else
      setTelefonos([])

  }, [actionCrud, itemCrud, itemsCrud])
  


  useEffect(() => {
    if (actionCrud === "create") {
      setItemCrudLocal(initialState);
    } else {
      setItemCrudLocal(itemCrud);
    }
  
    if (itemsCrud.length > 0) {
      const firstTelefono = itemsCrud[0]; // Seleccionar el primer domicilio
      setItemCrudLocal({
        id: firstTelefono.id,
        tipo: firstTelefono.tipo,
        numero: firstTelefono.numero,
      });
  
      setSelectedRow(0); // Marcar la primera fila como seleccionada
    }
  
    const _itemCrud = itemsCrud;
    if (_itemCrud.length > 0) {
      setTelefonos(_itemCrud);
    }
  }, [actionCrud, itemCrud, itemsCrud]);



  const handleObtieneTelefono = useCallback(
    (dataIndex) => {
      setSelectedRow(dataIndex)
      const selectedTelefono = telefonos[dataIndex]
      setItemCrudLocal({
        id: selectedTelefono.id,
        tipo: selectedTelefono.tipo,
        numero: selectedTelefono.numero,
      })
    },
    [telefonos],
  )

  const tableColumns = useMemo(
    () => [
      {
        name: "id",
        label: "ID",
        options: {
          filter: false,
          sort: false,
          display: "excluded",
        },
      },
      {
        name: "tipo",
        label: "Tipo de Teléfono",
        options: {
          filter: false,
          sort: false,
        },
      },
      {
        name: "numero",
        label: "N° de Teléfono",
        options: {
          filter: false,
          sort: false,
        },
      },
      {
        name: "actions",
        label: "Acciones",
        options: {
          filter: false,
          sort: false,
          empty: true,
          customBodyRenderLite: (dataIndex) => (
            <Tooltip title="Ver teléfono">
              <IconButton size="small" aria-label="view" onClick={() => handleObtieneTelefono(dataIndex)}>
                <RemoveRedEyeOutlinedIcon />
              </IconButton>
            </Tooltip>
          ),
        },
      },
    ],
    [handleObtieneTelefono],
  )

  const tableOptions = useMemo(
    () => ({
      download: false,
      filter: false,
      print: false,
      search: false,
      viewColumns: false,
      pagination: false,
      elevation: 1,
      selectableRows: "none",
      textLabels: {
        body: {
          noMatch: "No se encontraron registros",
        },
      },
      setRowProps: (row, dataIndex) => ({
        style: {
          backgroundColor: selectedRow === dataIndex ? "#e8f5e9" : "inherit",
        },
      }),
    }),
    [selectedRow],
  )

  const handleCloseDialog = () => {
    onClose()
  }

  const handleChange = (e) => {
    setItemCrudLocal({
      ...itemCrudLocal,
      [e.target.name]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    })
  }

  const handleSave = async () => {
    if (itemCrudLocal.tipo === "" || itemCrudLocal.numero === "") {
      setMessage({
        severity: "error",
        messageText: "Debes completar todos los campos.",
      })
      setShowMessage(true)
      return
    }
    const _itemCrud = {
      id: itemCrudLocal.id,
      tipo: itemCrudLocal.tipo,
      numero: itemCrudLocal.numero,
    }
    // Placeholder for save logic
    const response = { ok: true, messageText: "Saved successfully!", messageSeverity: "success" }

    if (response.ok) {
      onSave(_itemCrud)
    }

    setMessage({
      messageText: response.messageText,
      severity: response.messageSeverity,
    })
    setShowMessage(true)
  }

  const handleCopyToClipboard = () => {
    const formattedPhoneNumber = formatPhoneNumber(itemCrudLocal)
    navigator.clipboard
      .writeText(formattedPhoneNumber)
      .then(() => {
        console.log("Teléfono copiado")
        setMessage({
          severity: "success",
          messageText: "Número de teléfono copiado al portapapeles.",
        })
        setShowMessage(true)
      })
      .catch((err) => {
        console.error("Error al copiar el número de teléfono", err)
        setMessage({
          severity: "error",
          messageText: "Error al copiar el número de teléfono.",
        })
        setShowMessage(true)
      })
  }

  return (
    <Dialog
      open={true}
      onClose={handleCloseDialog}
      maxWidth="md"
      fullWidth={true}
      scroll="paper"
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">
        {actionCrud === "view" ? "Ver" : "Nuevo"} Teléfono
        <IconButton
          aria-label="close"
          onClick={handleCloseDialog}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "20vh",
            mb: 2,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 3,
              maxWidth: "800px",
              width: "100%",
              m: "5px",
              boxSizing: "border-box",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "20vh",
                mb: 4,
                marginTop: 2,
                overflowX: "auto",
              }}
            >
              <MUIDataTable
                title={"Listado de teléfonos"}
                columns={tableColumns}
                options={tableOptions}
                data={telefonos? telefonos : []}
              />
            </Box>

            <Grid container spacing={2}>
              {(actionCrud === "view" || actionCrud === "delete") && itemCrudLocal.tipo && (
                <Grid item xs={12} md={6}>
                  <Box display="flex" alignItems="center">
                    <h3 style={{ marginRight: "10px" }}>Tipo</h3>
                    <TextField
                      sx={{ width: "50%" }}
                      inputProps={{
                        autoComplete: "off",
                        readOnly: actionCrud === "view",
                      }}
                      id="tipo"
                      variant="outlined"
                      size="small"
                      name="tipo"
                      value={itemCrudLocal.tipo?.toUpperCase() || ""}
                      onChange={handleChange}
                    />
                  </Box>
                </Grid>
              )}
              {(actionCrud === "edit" || actionCrud === "new") && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth sx={{ mb: 1 }}>
                    <Autocomplete
                      id="tipo"
                      options={telefonos.map((tel) => tel.tipo)}
                      value={itemCrudLocal.tipo || null}
                      onChange={(event, newValue) => {
                        handleChange({
                          target: {
                            name: "tipo",
                            value: newValue || "",
                          },
                        })
                      }}
                      disabled={actionCrud === "view"}
                      renderInput={(params) => <TextField {...params} label="Tipo de teléfono" size="small" />}
                      ListboxProps={{ style: { maxHeight: "300px" } }}
                    />
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center">
                  <h3 style={{ marginRight: "10px" }}>Número</h3>
                  <TextField
                    sx={{ width: "100%" }}
                    inputProps={{
                      autoComplete: "off",
                      readOnly: actionCrud === "view",
                    }}
                    id="numero"
                    variant="outlined"
                    size="small"
                    name="numero"
                    value={itemCrudLocal.numero}
                    onChange={handleChange}
                  />
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button variant="contained" color="primary" onClick={handleCopyToClipboard}>
                Copiar teléfono
              </Button>
            </Box>

            <Box sx={{ textAlign: "right", mb: 2, mt: 2 }}>
              {actionCrud !== "view" && (
                <Button sx={{ mr: 1 }} variant="contained" onClick={handleSave} startIcon={<SaveIcon />}>
                  Guardar
                </Button>
              )}
              <Button sx={{ mr: 1 }} variant="outlined" onClick={handleCloseDialog}>
                {actionCrud === "view" ? "Volver" : "Cancelar"}
              </Button>
            </Box>
          </Paper>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default TelefonosDialog

